import {Component, EventEmitter, Input, OnInit, Output, ViewChild,} from '@angular/core';
import {NgForm} from '@angular/forms';
import {DomSanitizer, SafeResourceUrl} from '@angular/platform-browser';
import {finalize, forkJoin, map, Observable, of, switchMap, tap, throwError} from 'rxjs';
import {PersonUtils} from 'src/app/auth_profile/utils/v3/person-utils';
import * as LessonsV3 from 'src/app/col/model/v3/lessons';
import {TeacherRestServiceImpl} from 'src/app/col/services/teacher/teacher-rest-impl.service';
import {LessonTypeUtilsV3} from 'src/app/col/utils/lesson-utils-v3';
import {LessonProgressUtilsV3} from 'src/app/col/utils/v3/lesson-progress-utils';
import {Dates, TimeUnits} from 'src/app/utils/calendar-utils';
import {ModalComponent} from 'src/app/utils/modal/modal.component';
import {
  ContactType,
  CountryDefinition,
  V3Address,
  V3ContactEntry,
  V3PersonalSensitiveDetails,
  V3Settings
} from "../../../../auth_profile/model/v3/people";
import {NativeServiceApiProvider} from "../../../../services/native-api-provider.service";
import {catchError} from "rxjs/operators";
import {AuthorizationServiceProvider} from "../../../../auth_profile/services/authorization-service.provider";

@Component({
    selector: 'app-teacher-lesson-details-header',
    templateUrl: './teacher-lesson-details-header.component.html',
    styleUrls: ['./teacher-lesson-details-header.component.scss'],
    standalone: false
})
export class TeacherLessonDetailsHeaderComponent implements OnInit {
  @ViewChild('notificationConfirmationModal', { static: true })
  notificationConfirmationModal: ModalComponent;

  private _lessonDetails: LessonsV3.LessonView;
  private _teacherId: number;
  private _studentId: number;
  private _lessonId: number;
  private _currentProgress: LessonsV3.CallanProgressDetails | null = null;
  private doubleVideoReservationLocked = false;

  isComing = false;
  isRunning = false;
  readyToStart = false;
  allowNotification = true;
  needConfirmation = false;
  cancelable = false;
  mayBeStartedTime = false;
  shouldBeStarted = false;
  shouldBeFinished = false;
  isSkype = false;
  isComplete = false;
  canUpdatePlannedProgress = false;
  roomUrl: string | null = null;
  safeRoomUrl: SafeResourceUrl | null = null;
  studentTechnicalProfile: V3Settings;
  studentSkype: string | null = null;
  _clReason: string;
  countries: CountryDefinition[];
  plannedLessonFinish: Date;
  plannedLessonStart: Date;
  progressCommitLimit: Date;
  newLessonType: string;
  form: NgForm;
  studentContext: LessonsV3.ProductContextDetails | null;
  studentProfilePhoto: string
  studentSensitive: V3PersonalSensitiveDetails
  studentAddress: V3Address
  teacherSettings: V3Settings
  lessonStatus: LessonsV3.LessonStatus | null = null;
  lessonType: LessonsV3.LessonType | null = null;
  lessonProductVersion: string | null = null;
  lessonCourseName = '';
  lessonProductCode: string | null = null;
  lessonStudent: LessonsV3.Student | null = null;

  @Output() canUpdateProgress = new EventEmitter();
  @Output() updateLessonDetails = new EventEmitter();
  @Output() showCancelLesson = new EventEmitter();

  @Input()
  set currentProgress(progress: LessonsV3.CallanProgressDetails | null) {
    this._currentProgress = progress;
  }

  get currentProgress() {
    return this._currentProgress;
  }

  @Input()
  set lessonDetails(lessonDetails: LessonsV3.LessonView) {
    if (lessonDetails) {
      this._lessonDetails = lessonDetails;
      this.lessonStudent = this.extractLessonStudent(lessonDetails);
      this._studentId = this.lessonStudent?.id;
      this._lessonId = lessonDetails.id;
      this.setLessonDetails(lessonDetails);
      this.loadStudentContext().subscribe();
      this.loadStudentTechnicalProfile().subscribe();
      this.loadStudentData().subscribe();
      this.loadTeacherSettings().subscribe();
    }
  }

  get lessonDetails() {
    return this._lessonDetails;
  }

  @Input()
  set teacherId(teacherId: number) {
    this._teacherId = teacherId;
  }

  constructor(
    private sanitizer: DomSanitizer,
    private teacherRest: TeacherRestServiceImpl,
    private provider: NativeServiceApiProvider,
    private authProvider: AuthorizationServiceProvider
  ) {
    this.provider
      .casa()
      .pipe(switchMap((api) => api.listCountries()))
      .subscribe((countries) => (this.countries = countries));
  }

  ngOnInit(): void {}

  public getLessonDate(lesson: LessonsV3.LessonView) {
    return lesson.lessonMetric.plannedStartDate;
  }

  public getLessonStartedDate(lesson: LessonsV3.LessonView) {
    return lesson.lessonMetric.started;
  }

  public getRevisionPage() {
    return LessonProgressUtilsV3.proposedRevisionPage(this.currentProgress.nwp);
  }

  public getRecommendedReading() {
    if (!this.currentProgress || !this.currentProgress.nwp) {
      return 1;
    }
    return LessonProgressUtilsV3.proposedReadingPage(this.currentProgress.nwp);
  }

  public getLessonStudentPhotoUrl(): string {
    return this.studentProfilePhoto
  }

  public loadStudentData() {
    const studentPersonId = this.lessonStudent?.person?.id;
    if (!studentPersonId) {
      return throwError(
        () => new Error(`No student in lesson (${this.lessonDetails?.id})`)
      );
    }

    return this.provider.casa().pipe(
      switchMap(api => forkJoin([
        api.getPhoto(studentPersonId).pipe(
          catchError(() => of(null))
        ),
        api.getSensitive(studentPersonId).pipe(
          catchError(() => of(null))
        ),
        api.getAddress(studentPersonId).pipe(
          catchError(() => of(null))
        ),
      ])),
      tap(([photo, sensitive, address]) => {
        this.studentProfilePhoto = photo?.publicAccessUrl;
        this.studentSensitive = sensitive;
        this.studentAddress = address
      })
    );
  }

  loadTeacherSettings() {
    return forkJoin([
      this.provider.casa(),
      this.authProvider.getAuthDetailsService().pipe(
        switchMap(api => api.getSelfPersonId()))
    ]).pipe(
      switchMap(([api, personId]) => api.getSettings(personId)),
      tap(settings => this.teacherSettings = settings)
    )
  }

  public getStudentName() {
    if (!this.lessonStudent?.person) {
      return 'Unknown student';
    }
    return PersonUtils.getPersonName(this.lessonStudent.person);
  }

  getStudentNationality() {
    if (!this.countries || !this.studentSensitive) {
      return '';
    }

    const studentCountry = this.countries.find(
      (c) => c.name === this.studentSensitive.nationality
    );

    const locale = this.teacherSettings?.locale || 'en'
    return studentCountry ? studentCountry.translations[locale] : '';
  }

  getStudentCountry() {
    if (!this.countries || !this.studentAddress) {
      return '';
    }
    const studentCountry = this.countries.find(
      (c) => c.name === this.studentAddress.country
    );

    const locale = this.teacherSettings?.locale || 'en'
    return studentCountry ? studentCountry.translations[locale] : '';
  }

  getStudentGender() {
    const gender = this.studentSensitive?.gender;
    if (!gender) {
      return '';
    }
    return `PROFILE.FORM.PERSONAL_INFO.${gender.toUpperCase()}`;
  }

  reserveVideoClassroom() {
    if (this.doubleVideoReservationLocked) return;
    this.doubleVideoReservationLocked = true;
    this.teacherRest
      .createVideoClassroom(this._teacherId, this._lessonId)
      .pipe(tap(() => this.updateLessonDetails.emit()))
      .pipe(
        finalize(() => {
          this.doubleVideoReservationLocked = false;
        })
      )
      .subscribe();
  }

  sendStudentStartNotification() {
    this.allowNotification = false;
    this.provider.colV3().pipe(
      switchMap((api) => api.sendLessonNotification(this._lessonId))
    ).subscribe(() => {
      this.allowNotification = true;
      this.notificationConfirmationModal.show();
    });
  }

  confirm() {
    this.provider.colV3().pipe(
      switchMap((api) => api.updateLessonStatus(this._lessonId, LessonsV3.LessonStatus.Due)),
      tap(() => this.updateLessonDetails.emit())
    ).subscribe();
  }

  startLesson() {
    this.provider.colV3().pipe(
      switchMap((api) => api.updateLessonStatus(this._lessonId, LessonsV3.LessonStatus.InProgress)),
      tap(() => this.updateLessonDetails.emit())
    ).subscribe();
  }

  public showCancelLessonModal() {
    this._clReason = null;
    this.showCancelLesson.emit()
  }

  public enterTheRoom() {
    let observable: Observable<LessonsV3.LessonView | void | null> = of(null);
    if (this.lessonStatus === LessonsV3.LessonStatus.Due) {
      observable = this.provider.colV3().pipe(
        switchMap((api) => api.updateLessonStatus(this._lessonId, LessonsV3.LessonStatus.InProgress)),
        tap(() => this.updateLessonDetails.emit())
      );
    }
    observable.subscribe();
  }

  private setLessonDetails(lessonDetails: LessonsV3.LessonView) {
    type LegacyCompat = LessonsV3.LessonView & {
      lessonStatus?: LessonsV3.LessonStatus | string;
      lessonType?: LessonsV3.LessonType | string;
      roomUrl?: string;
      productVersion?: string;
      course?: LessonsV3.CourseRef & {
        name?: string;
        product?: LessonsV3.V3Product;
      };
    };
    const compat = lessonDetails as LegacyCompat;

    this.lessonStatus =
      (lessonDetails.status as LessonsV3.LessonStatus) ??
      (compat.lessonStatus as LessonsV3.LessonStatus) ??
      null;
    this.lessonType =
      (lessonDetails.type as LessonsV3.LessonType) ??
      (compat.lessonType as LessonsV3.LessonType) ??
      null;
    this.roomUrl = compat.roomUrl ?? null;
    this.lessonProductVersion = compat.productVersion ?? null;
    this.lessonCourseName = compat.course?.name ?? lessonDetails.course?.code ?? '';
    this.lessonProductCode = compat.course?.product?.code ?? null;

    const lessonStatus = this.lessonStatus;
    this.isComing =
      lessonStatus === LessonsV3.LessonStatus.Initializing ||
      lessonStatus === LessonsV3.LessonStatus.Booked ||
      lessonStatus === LessonsV3.LessonStatus.Due;
    this.readyToStart = lessonStatus === LessonsV3.LessonStatus.Due;
    this.isRunning = lessonStatus === LessonsV3.LessonStatus.InProgress;
    this.needConfirmation = lessonStatus === LessonsV3.LessonStatus.Booked;
    this.isComplete = lessonStatus === LessonsV3.LessonStatus.Complete;
    this.canUpdatePlannedProgress = this.isComing;
    this.plannedLessonStart = new Date(
      lessonDetails.lessonMetric.plannedStartDate
    );
    this.newLessonType = this.lessonType;
    this.safeRoomUrl = null;
    this.isSkype = false;
    if (this.roomUrl) {
      if (this.roomUrl.startsWith('skype:')) {
        this.isSkype = true;
        this.safeRoomUrl = this.sanitizer.bypassSecurityTrustResourceUrl(
          this.roomUrl
        );
      } else if (this.roomUrl.startsWith('video_room_url:')) {
        this.safeRoomUrl = this.sanitizer.bypassSecurityTrustResourceUrl(
          this.roomUrl.substring('video_room_url:'.length)
        );
      } else {
        this.safeRoomUrl = null;
      }
    }
    this.plannedLessonFinish = new Date(
      this.plannedLessonStart.getTime() +
        lessonDetails.lessonMetric.plannedDuration
    );
    this.progressCommitLimit = new Date(
      this.plannedLessonStart.getTime() + 1000 * 60 * 60 * 6
    );

    this.updateTimingVariables();
  }

  loadStudentTechnicalProfile(): Observable<V3Settings> {
    const studentPersonId = this.lessonStudent?.person?.id;
    if (!studentPersonId) {
      this.studentSkype = null;
      return of(null);
    }
    return this.provider
      .casa()
      .pipe(
        switchMap((api) =>
          forkJoin({
            settings: api.getSettings(studentPersonId),
            contacts: api
              .listContacts(studentPersonId)
              .pipe(catchError(() => of([] as V3ContactEntry[]))),
          })
        ),
        tap(({settings, contacts}) => {
          this.studentTechnicalProfile = settings;
          this.studentSkype = this.extractSkype(contacts);
        }),
        map(({settings}) => settings)
      );
  }

  private extractSkype(contacts: V3ContactEntry[]): string | null {
    const skypeContacts = contacts.filter(
      (entry) => entry.entryType === ContactType.SKYPE
    );
    if (skypeContacts.length === 0) {
      return null;
    }
    return (
      skypeContacts.find((entry) => entry.isDefault)?.value ??
      skypeContacts[0].value ??
      null
    );
  }

  loadStudentContext() {
    const productCode = this.lessonProductCode;
    if (!this._studentId || !productCode) {
      return of(null).pipe(tap(() => {
        this.studentContext = null
      }));
    }
    return forkJoin([
      this.provider.colV3(),
      this.authProvider
        .getAuthDetailsService()
        .pipe(switchMap((api) => api.getSelfSchoolId())),
    ]).pipe(
      switchMap(([api, schoolId]) =>
        api.getProductContexts(schoolId, this._studentId, productCode)
      ),
      map((contexts) => (contexts?.[0] ? contexts[0] : null)),
      tap((context) => {
        this.studentContext = context
      })
    );
  }

  updateTimingVariables(): void {
    if (!this.lessonDetails) {
      return;
    }
    const lessonStatus = this.lessonStatus;
    this.shouldBeStarted =
      this.plannedLessonStart.getTime() < new Date().getTime() && this.isComing;
    this.shouldBeFinished =
      this.plannedLessonFinish.getTime() < new Date().getTime() &&
      this.shouldBeStarted;
    this.canUpdateProgress.emit(
      this.shouldBeFinished ||
        this.isRunning ||
        this.shouldBeStarted ||
        (this.isComplete &&
          this.progressCommitLimit.getTime() > new Date().getTime())
    );

    this.mayBeStartedTime =
      Dates.diff(new Date(), this.plannedLessonStart) <
      TimeUnits.Minutes(15).toMilis();
    this.cancelable =
      (
        lessonStatus === LessonsV3.LessonStatus.Initializing ||
        lessonStatus === LessonsV3.LessonStatus.Booked
      ) &&
      !this.shouldBeStarted &&
      !this.mayBeStartedTime;
  }

  getInitials() {
    return PersonUtils.getInitials(this.lessonStudent?.person);
  }

  private extractLessonStudent(lesson: LessonsV3.LessonView): LessonsV3.Student | null {
    const lessonWithStudents = lesson as Partial<LessonsV3.LFWithStudents<LessonsV3.Student>>;
    return lessonWithStudents.students?.[0]?.reference ?? null;
  }

  getLessonStatusTranslation() {
    return LessonTypeUtilsV3.getStudentLessonStatus(this.lessonStatus)
  }

  buildVersionNumber(courseCode: string, productVersion: string) {
    if (!courseCode.startsWith("en.s") && !productVersion) return null;
    if (!productVersion) return '2nd Ed.';
    const intVer = parseInt(productVersion);
    if (isNaN(intVer)) return `v: ${productVersion}`;
    const lastDigit = intVer % 10;
    let postfix: string;
    if (intVer > 3 && intVer < 20) postfix = 'th';
    else if (lastDigit == 1) postfix = 'st';
    else if (lastDigit == 2) postfix = 'nd';
    else if (lastDigit == 3) postfix = 'rd';
    else postfix = 'th';
    return `${intVer}${postfix} Ed.`;
  }
  getLessonProductVersion(): string {
    return this.buildVersionNumber(this.lessonDetails.course.code, this.lessonProductVersion);
  }
  getProductVersion() {
    return this.buildVersionNumber(this.lessonDetails.course.code, this.studentContext?.productVersion);
  }

  isRedLabel() {
    return this.lessonDetails.course.code.startsWith('en.s')
      && this.studentContext?.productVersion == null
  }

  isLessonInOldVersion() {
    if (!this.lessonDetails.course.code.startsWith('en.s')) return false;
    return this.lessonProductVersion == null;

  }

}
